/**
 * BillingTab.jsx – v7
 * CHANGES:
 *   - Removed chargeAmounts override from generate modal and payload
 *     (charge type + amount now comes from each member's chargeAssignments)
 *   - Generate modal now shows only: billing month, due day, interest rate, GST rate
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, AlertCircle, Pencil, Check, X, CreditCard, ChevronDown, ChevronUp, Trash2, Printer } from 'lucide-react';
import {
  getBills, generateBills, getMembers,
  getBill, updateBill, createReceipt, deleteBill,
} from '../../api/societybook.api';
import Button        from '../../components/common/Button';
import Input         from '../../components/common/Input';
import Modal         from '../../components/common/Modal';
import BillPrintView from './BillPrint/BillPrintView';
import toast         from 'react-hot-toast';

const fmt = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_BADGE = {
  generated:      'badge-warning',
  draft:          'badge-subtle',
  partially_paid: 'badge-info',
  paid:           'badge-success',
  cancelled:      'badge-danger',
};

const STATUS_LABEL = {
  generated:      'Pending',
  draft:          'Draft',
  partially_paid: 'Partial',
  paid:           'Paid',
  cancelled:      'Cancelled',
};

const MONTHS = [
  { label: 'April',     short: 'Apr', value: 3  },
  { label: 'May',       short: 'May', value: 4  },
  { label: 'June',      short: 'Jun', value: 5  },
  { label: 'July',      short: 'Jul', value: 6  },
  { label: 'August',    short: 'Aug', value: 7  },
  { label: 'September', short: 'Sep', value: 8  },
  { label: 'October',   short: 'Oct', value: 9  },
  { label: 'November',  short: 'Nov', value: 10 },
  { label: 'December',  short: 'Dec', value: 11 },
  { label: 'January',   short: 'Jan', value: 0  },
  { label: 'February',  short: 'Feb', value: 1  },
  { label: 'March',     short: 'Mar', value: 2  },
];

const PAYMENT_MODES = ['neft', 'upi', 'rtgs', 'imps', 'cheque', 'cash', 'other'];

const EMPTY_RECEIPT = {
  amountReceived: '', receiptDate: new Date().toISOString().slice(0, 10),
  paymentMode: 'neft', instrumentNo: '', instrumentDate: '', bankName: '', remarks: '',
};

const getFYStartYear = (activeFY) => {
  if (!activeFY?.fyName) {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  }
  const match = activeFY.fyName.match(/^(\d{4})/);
  return match ? parseInt(match[1]) : new Date().getFullYear();
};

const calYearForMonth = (monthValue, fyStartYear) =>
  monthValue <= 2 ? fyStartYear + 1 : fyStartYear;

const lastDayOf = (year, month) => new Date(year, month + 1, 0).getDate();

const fmtPeriod = (from, to) => {
  if (!from) return '—';
  const f = new Date(from).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  if (!to) return f;
  const t = new Date(to).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  return f === t ? f : `${f} – ${t}`;
};

export default function BillingTab({ societyId, activeFY }) {
  const [members,      setMembers]      = useState([]);
  const [billsMap,     setBillsMap]     = useState({});
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState({});

  // Generate modal
  const [showGen,    setShowGen]    = useState(false);
  const [generating, setGenerating] = useState(false);
  const fyStartYear = getFYStartYear(activeFY);
  const [selMonth,  setSelMonth]   = useState(() => new Date().getMonth());
  const [genForm,   setGenForm]    = useState({ dueDay: 15, interestRate: 0, gstRate: 0 });

  // Regenerate decision modal
  const [showRegenModal,    setShowRegenModal]    = useState(false);
  const [dupMembers,        setDupMembers]        = useState([]);
  const [newMembers,        setNewMembers]        = useState([]);
  const [selectedMembers,   setSelectedMembers]   = useState([]);
  const [regenMode,         setRegenMode]         = useState('');
  const [pendingGenPayload, setPendingGenPayload] = useState(null);

  // Edit bill
  const [editBillId,  setEditBillId]  = useState(null);
  const [editAmounts, setEditAmounts] = useState({});
  const [editSaving,  setEditSaving]  = useState(false);

  // Payment modal
  const [payMember,     setPayMember]     = useState(null);
  const [receiptForm,   setReceiptForm]   = useState(EMPTY_RECEIPT);
  const [pendingBills,  setPendingBills]  = useState([]);
  const [billsLoading,  setBillsLoading]  = useState(false);
  const [receiptSaving, setReceiptSaving] = useState(false);

  // Print
  const [printBill,     setPrintBill]     = useState(null);
  const [printAllBills, setPrintAllBills] = useState(null);
  const [showPrintMenu, setShowPrintMenu] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mRes, bRes] = await Promise.all([
        getMembers(societyId),
        getBills(societyId, {}),
      ]);
      const mList = mRes.data.data || [];
      const bList = bRes.data.data || [];
      const map = {};
      mList.forEach(m => { map[m._id] = []; });
      bList.forEach(b => {
        const mid = b.memberId?.toString();
        if (map[mid]) map[mid].push(b);
      });
      Object.keys(map).forEach(mid => {
        map[mid].sort((a, b) => new Date(b.billDate) - new Date(a.billDate));
      });
      setMembers(mList);
      setBillsMap(map);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [societyId]);

  useEffect(() => { load(); }, [load]);

  // ── Open Generate modal ───────────────────────────────────────────────────
  const openGenerate = () => setShowGen(true);

  // ── Date range ────────────────────────────────────────────────────────────
  const getDateRange = () => {
    const calYear  = calYearForMonth(selMonth, fyStartYear);
    const lastDay  = lastDayOf(calYear, selMonth);
    const mm       = String(selMonth + 1).padStart(2, '0');
    const periodFrom = `${calYear}-${mm}-01`;
    const periodTo   = `${calYear}-${mm}-${String(lastDay).padStart(2, '0')}`;
    const billDate   = periodFrom;
    const dueDay     = Math.min(parseInt(genForm.dueDay) || 15, lastDay);
    const dueDate    = `${calYear}-${mm}-${String(dueDay).padStart(2, '0')}`;
    return { billDate, periodFrom, periodTo, dueDate, calYear };
  };

  // ── Step 1: Check for duplicates before generating ────────────────────────
  const handleGenerate = async () => {
    const { billDate, periodFrom, periodTo, dueDate } = getDateRange();

    const payload = {
      societyId,
      billDate, dueDate, periodFrom, periodTo,
      interestRate: parseFloat(genForm.interestRate) || 0,
      gstRate:      parseFloat(genForm.gstRate) || 0,
    };

    try {
      setGenerating(true);
      const bRes = await getBills(societyId, { periodFrom });
      const existingBills = bRes.data.data || [];
      const existingMemberIds = new Set(existingBills.map(b => b.memberId?.toString()));

      const alreadyHaveBill = members.filter(m => existingMemberIds.has(m._id.toString()));
      const dontHaveBill    = members.filter(m => !existingMemberIds.has(m._id.toString()));

      if (alreadyHaveBill.length === 0) {
        await runGenerate({ ...payload, memberIds: null, forceOverride: false });
        return;
      }

      setDupMembers(alreadyHaveBill);
      setNewMembers(dontHaveBill);
      setSelectedMembers(alreadyHaveBill.map(m => m._id));
      setRegenMode('all');
      setPendingGenPayload(payload);
      setShowGen(false);
      setShowRegenModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── Step 2: Execute generation after decision ─────────────────────────────
  const runGenerate = async ({ memberIds, forceOverride, ...rest }) => {
    try {
      setGenerating(true);

      if (forceOverride && memberIds?.length > 0) {
        const { periodFrom } = rest;
        const bRes = await getBills(societyId, { periodFrom });
        const toDelete = (bRes.data.data || []).filter(b =>
          memberIds.includes(b.memberId?.toString())
        );
        await Promise.all(toDelete.map(b => deleteBill(b._id)));
      }

      const res = await generateBills({
        ...rest,
        societyId,
        memberIds: memberIds || null,
        forceOverride: false,
      });

      toast.success(`${res.data.generated} bill(s) generated${res.data.emailsSent > 0 ? `, ${res.data.emailsSent} email(s) sent` : ''}`);
      setShowGen(false);
      setShowRegenModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── Confirm regenerate from decision modal ────────────────────────────────
  const handleRegenConfirm = async () => {
    if (!pendingGenPayload) return;

    if (regenMode === 'all') {
      await runGenerate({ ...pendingGenPayload, memberIds: members.map(m => m._id), forceOverride: true });
    } else if (regenMode === 'select') {
      const allIds = [...selectedMembers, ...newMembers.map(m => m._id)];
      await runGenerate({ ...pendingGenPayload, memberIds: allIds, forceOverride: true });
    } else if (regenMode === 'new') {
      if (newMembers.length === 0) { toast('All members already have bills for this period.'); setShowRegenModal(false); return; }
      await runGenerate({ ...pendingGenPayload, memberIds: newMembers.map(m => m._id), forceOverride: false });
    }
  };

  const toggleMemberSelect = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Edit bill ─────────────────────────────────────────────────────────────
  const startEdit = (bill) => {
    setEditBillId(bill._id);
    const amounts = {};
    (bill.billLines || []).forEach(l => { amounts[l.chargeHeadId] = l.amount; });
    setEditAmounts(amounts);
  };

  const saveEdit = async (bill) => {
    setEditSaving(true);
    try {
      await updateBill(bill._id, { chargeAmounts: editAmounts });
      toast.success('Bill updated');
      setEditBillId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setEditSaving(false); }
  };

  // ── Payment ───────────────────────────────────────────────────────────────
  const openPayment = async (member) => {
    setPayMember(member);
    setReceiptForm({ ...EMPTY_RECEIPT });
    setBillsLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        getBills(societyId, { memberId: member._id, status: 'generated' }),
        getBills(societyId, { memberId: member._id, status: 'partially_paid' }),
      ]);
      const all = [...(r1.data.data || []), ...(r2.data.data || [])]
        .sort((a, b) => new Date(a.billDate) - new Date(b.billDate));
      setPendingBills(all);
      const total = all.reduce((s, b) => s + (b.netPayable || 0), 0);
      setReceiptForm(f => ({ ...f, amountReceived: total.toFixed(2) }));
    } catch { toast.error('Failed to load pending bills'); }
    finally { setBillsLoading(false); }
  };

  const handleReceiptSave = async () => {
    if (!receiptForm.amountReceived || parseFloat(receiptForm.amountReceived) <= 0) {
      toast.error('Enter a valid amount'); return;
    }
    setReceiptSaving(true);
    try {
      const res = await createReceipt({
        societyId,
        memberId:       payMember._id,
        amountReceived: parseFloat(receiptForm.amountReceived),
        receiptDate:    receiptForm.receiptDate,
        paymentMode:    receiptForm.paymentMode,
        instrumentNo:   receiptForm.instrumentNo,
        instrumentDate: receiptForm.instrumentDate || undefined,
        bankName:       receiptForm.bankName,
        remarks:        receiptForm.remarks,
      });
      toast.success(`Receipt #${res.data.data?.receiptNo} recorded`);
      setPayMember(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed'); }
    finally { setReceiptSaving(false); }
  };

  // ── Delete bill ───────────────────────────────────────────────────────────
  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    try { await deleteBill(billId); toast.success('Bill deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  // ── Print all ─────────────────────────────────────────────────────────────
  const handlePrintAll = async (mode, periodFrom) => {
    setShowPrintMenu(false);
    try {
      let billsToPrint = [];
      if (mode === 'latest') {
        billsToPrint = members.map(m => (billsMap[m._id] || [])[0]).filter(Boolean);
      } else {
        const res = await getBills(societyId, { periodFrom });
        billsToPrint = res.data.data || [];
      }
      if (billsToPrint.length === 0) { toast.error('No bills found'); return; }
      const fullBills = await Promise.all(
        billsToPrint.map(b => getBill(b._id).then(r => r.data.data))
      );
      setPrintAllBills(fullBills);
    } catch { toast.error('Failed to load bills'); }
  };

  const sf  = k => e => setGenForm(f => ({ ...f, [k]: e.target.value }));
  const srf = k => e => setReceiptForm(f => ({ ...f, [k]: e.target.value }));

  const { billDate, periodFrom, periodTo, dueDate, calYear } = getDateRange();
  const selMonthLabel = MONTHS.find(m => m.value === selMonth)?.label || '';

  // ── Stats ─────────────────────────────────────────────────────────────────
  const allLatest    = members.map(m => (billsMap[m._id] || [])[0]).filter(Boolean);
  const totalBilled  = allLatest.reduce((s, b) => s + (b.totalDue || 0), 0);
  const totalPending = allLatest.filter(b => ['generated', 'partially_paid'].includes(b.status)).length;
  const totalPaid    = allLatest.filter(b => b.status === 'paid').length;

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Members', value: members.length,  accent: 'var(--gold)'    },
          { label: 'Pending',       value: totalPending,    accent: 'var(--warning)' },
          { label: 'Paid',          value: totalPaid,       accent: 'var(--success)' },
          { label: 'Total Billed',  value: `₹${Number(totalBilled).toLocaleString('en-IN')}`, accent: 'var(--info)' },
        ].map(s => (
          <div key={s.label} className="fy-stat-card" style={{ '--accent': s.accent }}>
            <div className="fy-stat-top"><span className="fy-stat-label">{s.label}</span></div>
            <div className="fy-stat-value" style={{ fontSize: s.label === 'Total Billed' ? '1.2rem' : undefined }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Member Bills Table ── */}
      <div className="section-card">
        <div className="section-card-head">
          <h3>Maintenance Bills</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ position: 'relative' }}>
              <Button size="sm" variant="ghost" icon={Printer} onClick={() => setShowPrintMenu(p => !p)}>Print All</Button>
              {showPrintMenu && (
                <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, minWidth: 210, padding: '0.5rem 0' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)', padding: '0.3rem 0.85rem 0.4rem' }}>Print Options</div>
                  <button onClick={() => handlePrintAll('latest')}
                    style={{ width: '100%', padding: '0.5rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.83rem', color: 'var(--text)' }}>
                    Latest bill per member
                  </button>
                  <div style={{ height: 1, background: 'var(--border-subtle)', margin: '0.25rem 0' }} />
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)', padding: '0.4rem 0.85rem 0.25rem' }}>By Month</div>
                  {[...new Set(
                    Object.values(billsMap).flat()
                      .map(b => b.periodFrom?.slice(0, 7))
                      .filter(Boolean)
                  )].sort().reverse().map(period => (
                    <button key={period} onClick={() => handlePrintAll('period', period + '-01')}
                      style={{ width: '100%', padding: '0.4rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.83rem', color: 'var(--text)' }}>
                      {new Date(period + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" icon={Plus} onClick={openGenerate}>Generate Bills</Button>
          </div>
        </div>

        {members.length === 0 ? (
          <div className="empty-state" style={{ padding: '3rem' }}>
            <AlertCircle size={36} strokeWidth={1} />
            <h3>No members found</h3>
            <p>Add members first before generating bills.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Flat</th>
                  <th>Member</th>
                  <th>Latest Bill Period</th>
                  <th>Due Date</th>
                  <th style={{ textAlign: 'right' }}>Billed</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => {
                  const mBills    = billsMap[member._id] || [];
                  const latest    = mBills[0] || null;
                  const history   = mBills.slice(1);
                  const isOpen    = expanded[member._id];
                  const isEditing = editBillId === latest?._id;

                  return (
                    <React.Fragment key={member._id}>
                      <tr
                        style={{ cursor: history.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => history.length > 0 && setExpanded(p => ({ ...p, [member._id]: !p[member._id] }))}
                      >
                        <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center', paddingRight: 0 }}>
                          {history.length > 0 && (
                            <button
                              onClick={() => setExpanded(p => ({ ...p, [member._id]: !p[member._id] }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem', display: 'flex', alignItems: 'center' }}>
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          )}
                        </td>
                        <td><span className="flat-tag">{member.flatNo}</span></td>
                        <td style={{ fontSize: '0.85rem', fontWeight: 500 }}>{member.memberName}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {latest ? fmtPeriod(latest.periodFrom, latest.periodTo) : <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>No bill yet</span>}
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {latest ? new Date(latest.dueDate).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td className="amount-cell">
                          {latest ? `₹${fmt(latest.totalDue)}` : '—'}
                        </td>
                        <td className="amount-cell" style={{ fontWeight: 600, color: latest?.netPayable > 0 ? 'var(--danger)' : latest ? 'var(--success)' : 'var(--text-dim)' }}>
                          {latest ? `₹${fmt(latest.netPayable)}` : '—'}
                        </td>
                        <td>
                          {latest
                            ? <span className={`badge ${STATUS_BADGE[latest.status] || 'badge-subtle'}`}>{STATUS_LABEL[latest.status] || latest.status}</span>
                            : <span className="badge badge-subtle">No Bills</span>
                          }
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '0.3rem' }}>
                              <Button size="xs" variant="success" icon={Check} loading={editSaving} onClick={() => saveEdit(latest)} />
                              <Button size="xs" variant="subtle" icon={X} onClick={() => setEditBillId(null)} />
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                              {latest && latest.status !== 'paid' && (
                                <Button size="xs" variant="success" icon={CreditCard} onClick={() => openPayment(member)}>Pay</Button>
                              )}
                              {latest && latest.status !== 'paid' && (
                                <Button size="xs" variant="ghost" icon={Pencil} onClick={() => startEdit(latest)} />
                              )}
                              {latest && latest.status !== 'paid' && (
                                <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDeleteBill(latest._id)} />
                              )}
                              {latest && (
                                <Button size="xs" variant="ghost" onClick={() => { getBill(latest._id).then(r => setPrintBill(r.data.data)); }}>Print</Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>

                      {isEditing && latest && (
                        <tr style={{ background: 'var(--bg-overlay)' }}>
                          <td colSpan={9} style={{ padding: '0.75rem 1.25rem' }}>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginRight: '0.5rem' }}>Edit charges:</span>
                              {(latest.billLines || []).map(l => (
                                <div key={l.chargeHeadId} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                  <label style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{l.chargeHeadName}</label>
                                  <input type="number" value={editAmounts[l.chargeHeadId] ?? l.amount}
                                    onChange={e => setEditAmounts(a => ({ ...a, [l.chargeHeadId]: e.target.value }))}
                                    style={{ width: 80, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', padding: '4px 8px', fontSize: '0.82rem', color: 'var(--text)' }} />
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}

                      {isOpen && history.map((hb, idx) => (
                        <tr key={hb._id} style={{ background: 'var(--bg-overlay)' }}>
                          <td></td>
                          <td><span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', paddingLeft: '0.5rem' }}>#{hb.billNo}</span></td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {idx === 0 && <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)', fontStyle: 'italic', marginRight: '0.4rem' }}>history</span>}
                          </td>
                          <td style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{fmtPeriod(hb.periodFrom, hb.periodTo)}</td>
                          <td style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>{new Date(hb.dueDate).toLocaleDateString('en-IN')}</td>
                          <td className="amount-cell" style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>₹{fmt(hb.totalDue)}</td>
                          <td className="amount-cell" style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>₹{fmt(hb.netPayable)}</td>
                          <td><span className={`badge ${STATUS_BADGE[hb.status] || 'badge-subtle'}`} style={{ fontSize: '0.68rem' }}>{STATUS_LABEL[hb.status] || hb.status}</span></td>
                          <td><Button size="xs" variant="ghost" onClick={() => { getBill(hb._id).then(r => setPrintBill(r.data.data)); }}>Print</Button></td>
                        </tr>
                      ))}

                      {isOpen && (
                        <tr><td colSpan={9} style={{ padding: 0, height: 2, background: 'var(--border-subtle)', opacity: 0.3 }} /></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Generate Bills Modal ── */}
      <Modal
        isOpen={showGen}
        onClose={() => setShowGen(false)}
        title="Generate Maintenance Bills"
        subtitle="Bills are generated using each member's charge assignments"
        size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowGen(false)}>Cancel</Button>
            <Button loading={generating} icon={Plus} onClick={handleGenerate}>Generate Bills</Button>
          </>
        }>
        <div className="txn-form">
          <div className="field">
            <label className="field-label">Billing Month</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
              {MONTHS.map(m => {
                const active = selMonth === m.value;
                return (
                  <button key={m.value} type="button" onClick={() => setSelMonth(m.value)}
                    style={{ padding: '0.35rem 0.75rem', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem', border: `1px solid ${active ? 'var(--gold)' : 'var(--border-subtle)'}`, background: active ? 'var(--gold-glow)' : 'var(--bg-raised)', color: active ? 'var(--gold)' : 'var(--text-muted)', fontWeight: active ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s ease' }}>
                    {m.short}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ padding: '0.7rem 1rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
              {[
                { label: 'Month',     value: `${selMonthLabel} ${calYear}`, color: 'var(--gold)' },
                { label: 'Period',    value: `${periodFrom} → ${periodTo}` },
                { label: 'Bill Date', value: billDate },
                { label: 'Due Date',  value: dueDate  },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</div>
                  <div style={{ fontWeight: item.color ? 700 : 500, color: item.color || 'var(--text)', marginTop: '0.15rem' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <Input label="Due Day of Month" type="number" min="1" max="28"
            value={genForm.dueDay} onChange={sf('dueDay')} placeholder="e.g. 15" />

          <div className="txn-form-grid">
            <Input label="Interest Rate (% p.a.)" type="number"
              value={genForm.interestRate} onChange={sf('interestRate')} placeholder="0" />
            <Input label="GST Rate (%)" type="number"
              value={genForm.gstRate} onChange={sf('gstRate')} placeholder="0" />
          </div>

          <div style={{ padding: '0.65rem 0.9rem', background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--info)' }}>
            Charge amounts are read from each member's Charge Assignments (Members → Charges).
          </div>
        </div>
      </Modal>

      {/* ── Regenerate Decision Modal ── */}
      <Modal
        isOpen={showRegenModal}
        onClose={() => setShowRegenModal(false)}
        title="Bills Already Exist"
        subtitle={`${dupMembers.length} member(s) already have bills for ${selMonthLabel} ${calYear}`}
        size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowRegenModal(false)}>Cancel</Button>
            <Button loading={generating} onClick={handleRegenConfirm} variant={regenMode === 'all' ? 'danger' : 'primary'}>
              {regenMode === 'all'    ? 'Regenerate for All'
               : regenMode === 'select' ? `Regenerate for ${selectedMembers.length} Member(s)`
               : `Generate for ${newMembers.length} New Member(s)`}
            </Button>
          </>
        }>
        <div className="txn-form">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[
              { key: 'all',    title: 'Regenerate for All Members',  desc: 'Delete existing bills and regenerate fresh for all members.',                         color: 'var(--danger)'  },
              { key: 'select', title: 'Select Specific Members',     desc: 'Choose which members to regenerate. New members will also be included.',              color: 'var(--gold)'    },
              { key: 'new',    title: 'New Members Only',            desc: newMembers.length > 0 ? `Generate only for ${newMembers.length} member(s) who don't have a bill yet.` : 'All members already have bills for this period.', color: 'var(--success)', disabled: newMembers.length === 0 },
            ].map(opt => (
              <div key={opt.key} onClick={() => !opt.disabled && setRegenMode(opt.key)}
                style={{ padding: '0.85rem 1rem', borderRadius: 'var(--radius-sm)', cursor: opt.disabled ? 'not-allowed' : 'pointer', border: `2px solid ${regenMode === opt.key ? opt.color : 'var(--border-subtle)'}`, background: regenMode === opt.key ? 'var(--bg-overlay)' : 'var(--bg-raised)', opacity: opt.disabled ? 0.4 : 1, transition: 'all 0.15s ease' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: regenMode === opt.key ? opt.color : 'var(--text)' }}>{opt.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{opt.desc}</div>
              </div>
            ))}
          </div>

          {regenMode === 'select' && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Select members to regenerate:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 220, overflowY: 'auto' }}>
                {dupMembers.map(m => (
                  <label key={m._id}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.83rem' }}>
                    <input type="checkbox" checked={selectedMembers.includes(m._id)} onChange={() => toggleMemberSelect(m._id)} />
                    <span className="flat-tag" style={{ fontSize: '0.72rem' }}>{m.flatNo}</span>
                    <span>{m.memberName}</span>
                  </label>
                ))}
              </div>
              {newMembers.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '0.5rem' }}>
                  + {newMembers.length} new member(s) without bills will also be generated automatically.
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Payment Modal ── */}
      <Modal
        isOpen={!!payMember}
        onClose={() => setPayMember(null)}
        title="Record Payment"
        subtitle={payMember ? `${payMember.flatNo} – ${payMember.memberName}` : ''}
        size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setPayMember(null)}>Cancel</Button>
            <Button loading={receiptSaving} icon={CreditCard} onClick={handleReceiptSave}>Save Receipt</Button>
          </>
        }>
        <div className="txn-form">
          {billsLoading ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Loading pending bills…</div>
          ) : pendingBills.length === 0 ? (
            <div style={{ padding: '0.65rem 0.9rem', background: 'var(--success-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.82rem', color: 'var(--success)', fontWeight: 600 }}>
              No pending dues for this member.
            </div>
          ) : (
            <div style={{ background: 'var(--warning-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(245,200,66,0.15)', padding: '0.85rem 1rem', marginBottom: '0.25rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--warning)', marginBottom: '0.5rem' }}>Pending Bills</div>
              {pendingBills.map(b => (
                <div key={b._id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0', borderBottom: '1px solid rgba(245,200,66,0.1)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Bill #{b.billNo} · {fmtPeriod(b.periodFrom, b.periodTo)}</span>
                  <span style={{ fontWeight: 600, color: 'var(--danger)' }}>₹{fmt(b.netPayable)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '0.875rem', marginTop: '0.5rem', color: 'var(--warning)' }}>
                <span>Total Outstanding</span>
                <span>₹{fmt(pendingBills.reduce((s, b) => s + (b.netPayable || 0), 0))}</span>
              </div>
            </div>
          )}
          <div className="txn-form-grid">
            <Input label="Amount Received (₹) *" type="number"
              value={receiptForm.amountReceived} onChange={srf('amountReceived')} placeholder="0.00" />
            <Input label="Receipt Date *" type="date"
              value={receiptForm.receiptDate} onChange={srf('receiptDate')} />
          </div>
          <div className="txn-form-grid">
            <div className="field">
              <label className="field-label">Payment Mode</label>
              <div className="field-control">
                <select className="field-input" value={receiptForm.paymentMode} onChange={srf('paymentMode')}>
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <Input label="Cheque / UTR / Ref No" value={receiptForm.instrumentNo}
              onChange={srf('instrumentNo')} placeholder="Transaction reference" />
          </div>
          {receiptForm.paymentMode === 'cheque' && (
            <Input label="Instrument Date" type="date"
              value={receiptForm.instrumentDate} onChange={srf('instrumentDate')} />
          )}
          <Input label="Bank Name" value={receiptForm.bankName} onChange={srf('bankName')} placeholder="e.g. ICICI Bank" />
          <Input label="Remarks" value={receiptForm.remarks} onChange={srf('remarks')} />
        </div>
      </Modal>

      {printBill     && <BillPrintView bill={printBill}       onClose={() => setPrintBill(null)} />}
      {printAllBills && <BillPrintView bills={printAllBills}  onClose={() => setPrintAllBills(null)} />}
    </div>
  );
}
